import { createFileRoute, redirect, useSearch } from "@tanstack/react-router";
import { DashboardLayout } from "@/components/dashboard-layout";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GatewaysTable } from "./-components/gateways-table";
import { useCallback, useMemo, useState, useEffect } from "react";
import { deleteGatewayFn, gatewaysQueryOptions } from "@/server/gateways";
import { toast } from "sonner";
import { CrudEntry } from "@/lib/mma/types.gen";
import {
  checkIfUserMayRead,
  checkIfUserMayWrite,
  checkIfUserMayDelete,
  ContextUser,
  getNextRedirectRoute,
} from "@/lib/utils";
import { GatewayDetails } from "./-components/gateway-details";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { ConfirmDialog } from "@/components/confirm-dialog";

export const Route = createFileRoute("/_dashboard/gateways")({
  component: () => (
    <DashboardLayout submenu={null}>
      <GatewaysRouteComponent />
    </DashboardLayout>
  ),
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "GATEWAY");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
  },
  loader: ({ context }) => {
    return {
      crumb: "Gateways",
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
      user: context.user as ContextUser | null | undefined,
    };
  },
});

function GatewaysRouteComponent() {
  const { userProfileId, accessControl, user } = Route.useLoaderData();
  const searchParams = useSearch({ strict: false }) as {
    gatewayId?: string;
    tab?: string;
  };
  const openRightSidebar = useRightSidebarOpen();
  const closeRightSidebar = useRightSidebarClose();
  const queryClient = useQueryClient();

  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "GATEWAY");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "GATEWAY");
  const [staleTime, setStaleTime] = useState(1000 * 60);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gatewayToDelete, setGatewayToDelete] = useState<number | null>(null);

  useEffect(() => {
    const savedStaleTime = localStorage.getItem("staleTime");
    if (savedStaleTime) {
      setStaleTime(Number(savedStaleTime));
    }
  }, []);

  const queryOptions = useMemo(() => gatewaysQueryOptions(staleTime), [staleTime]);

  const gatewaysQuery = useQuery(queryOptions);
  const gateways = gatewaysQuery.data;

  const handleStaleTimeChange = (value: string) => {
    setStaleTime(Number(value));
    localStorage.setItem("staleTime", value);
  };

  useEffect(() => {
    if (gatewaysQuery.isError) {
      const message =
        gatewaysQuery.error instanceof Error
          ? gatewaysQuery.error.message
          : "Failed to load gateways. Please try again later.";
      toast.error(message);
    }
  }, [gatewaysQuery.isError, gatewaysQuery.error]);

  const closeAfterSave = useCallback(() => {
    closeRightSidebar(["gatewayId", "tab"]);
  }, [closeRightSidebar]);

  const deleteGatewayMutation = useMutation({
    mutationFn: deleteGatewayFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gateways"] });
      closeAfterSave();
    },
  });

  const handleDeleteClick = useCallback((id: number) => {
    setGatewayToDelete(id);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (!gatewayToDelete) return;
    try {
      const deleteGatewayPromise = deleteGatewayMutation.mutateAsync({ data: { id: gatewayToDelete } });
      toast.promise(deleteGatewayPromise, {
        loading: "Deleting gateway...",
        success: () => "Gateway deleted",
        error: (error) => error.message || "Failed to delete gateway",
      });
      await deleteGatewayPromise;
    } finally {
      setDeleteDialogOpen(false);
      setGatewayToDelete(null);
    }
  };

  useEffect(() => {
    const componentToRender = searchParams.gatewayId ? (
      <GatewayDetails
        gatewayId={searchParams.gatewayId === "new" ? null : Number(searchParams.gatewayId)}
        deleteGateway={handleDeleteClick}
        loading={gatewaysQuery.isLoading}
        user={user}
      />
    ) : null;

    const hasPermission = searchParams.gatewayId !== "new" || mayWrite;
    openRightSidebar({
      component: componentToRender,
      paramKeys: ["gatewayId", "tab"],
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeRightSidebar(["gatewayId", "tab"]),
    });
  }, [
    searchParams.gatewayId,
    gatewaysQuery.isLoading,
    mayWrite,
    handleDeleteClick,
    openRightSidebar,
    closeRightSidebar,
  ]);

  return (
    <>
      <GatewaysTable
        gateways={gateways}
        staleTime={staleTime}
        handleStaleTimeChange={handleStaleTimeChange}
        isLoading={gatewaysQuery.isLoading}
        mayWrite={mayWrite}
        mayDelete={mayDelete}
        handleDeleteClick={handleDeleteClick}
        isDeletePending={deleteGatewayMutation.isPending}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete gateway"
        description="Are you sure you want to delete this gateway? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setGatewayToDelete(null);
        }}
        loading={deleteGatewayMutation.isPending}
      />
    </>
  );
}
